import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Salary.css";

function SalaryList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchEmployeePayStructure();
  }, []);

  const fetchEmployeePayStructure = async () => {
    try {
      const response = await api.get("/employee-pay-structure");
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee salary data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (employee) => {
    setViewEmployee(employee);
    setShowModal(true);
  };

  const handleAddPayStructure = (userId) => {
    navigate(`/salary/add/${userId}`);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") {
      return "N/A";
    }

    return `Rs. ${new Intl.NumberFormat("en-IN").format(amount)}/-`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const activeStructures = useMemo(
    () => employees.filter((employee) => employee.emp_active_paystructure),
    [employees],
  );

  const payrollTotal = useMemo(
    () =>
      activeStructures.reduce(
        (sum, employee) =>
          sum + Number(employee.emp_active_paystructure?.ctc_yearly || 0),
        0,
      ),
    [activeStructures],
  );

  const salaryColumns = [
    {
      header: "Emp. ID",
      key: "code",
    },
    {
      header: "Name",
      render: (employee) => `${employee.first_name} ${employee.last_name}`,
    },
    {
      header: "Designation",
      render: (employee) =>
        employee.user_designation?.designation_detail?.designation_name || "N/A",
    },
    {
      header: "Basic Pay (Monthly)",
      render: (employee) =>
        employee.emp_active_paystructure
          ? formatCurrency(employee.emp_active_paystructure.basic_monthly)
          : "N/A",
    },
    {
      header: "CTC (Yearly)",
      render: (employee) =>
        employee.emp_active_paystructure
          ? formatCurrency(employee.emp_active_paystructure.ctc_yearly)
          : "N/A",
    },
    {
      header: "Version Month",
      render: (employee) =>
        employee.emp_active_paystructure
          ? formatDate(employee.emp_active_paystructure.effective_from)
          : "N/A",
    },
    {
      header: "Actions",
      render: (employee) => (
        <CommonActionMenu
          ariaLabel={`Actions for employee ${employee.code}`}
          actions={[
            {
              label: "View Details",
              icon: "fas fa-eye",
              onClick: () => handleViewDetails(employee),
            },
            {
              label: "Add New Structure",
              icon: "fas fa-plus",
              onClick: () => handleAddPayStructure(employee.id),
            },
          ]}
        />
      ),
    },
  ];

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading salary data..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="salary-list-page salary-list-shell">
        <div className="salary-list-hero">
          <div className="salary-list-hero-copy">
            <span className="salary-advances-eyebrow">Compensation records</span>
            <h1>Salary Breakdown</h1>
            <p>
              Review employee pay structures, track the latest version month, and
              create revised structures from one clean register.
            </p>
          </div>
          <div className="salary-list-stats">
            <div className="salary-list-stat-card">
              <span>Employees</span>
              <strong>{employees.length}</strong>
            </div>
            <div className="salary-list-stat-card">
              <span>Active structures</span>
              <strong>{activeStructures.length}</strong>
            </div>
            <div className="salary-list-stat-card">
              <span>Total yearly CTC</span>
              <strong>{formatCurrency(payrollTotal)}</strong>
            </div>
          </div>
        </div>

        <div className="table-card salary-list-card">
          <div className="salary-list-card-header">
            <div>
              <span className="section-kicker">Employee register</span>
              <h4>Salary Breakdown</h4>
              <p>
                Open the employee detail sheet to inspect allowances, deductions,
                and prior employment financials.
              </p>
            </div>
          </div>
          <div className="salary-structure-notice">
            <strong>Notice:</strong> The employee salary structure cannot be edited. To make any
            changes or amendment create salary structure.
          </div>
          <CommonTable
            columns={salaryColumns}
            data={employees}
            tableClassName="salary-table salary-list-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No employees found"
            searchPlaceholder="Search employees..."
            getSearchText={(employee) =>
              [
                employee.code,
                employee.first_name,
                employee.last_name,
                employee.user_designation?.designation_detail?.designation_name,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>
      </div>

      {showModal && viewEmployee && (
        <ViewSalaryModal
          employee={viewEmployee}
          onClose={() => setShowModal(false)}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}
    </MainLayout>
  );
}

function ViewSalaryModal({ employee, onClose, formatCurrency, formatDate }) {
  const payStructure = employee.emp_active_paystructure;
  const allowances = employee.allowances || [];
  const deductions = employee.deductions || [];
  const monthlyGross = Number(employee.total_allowances || 0) / 12 + Number(payStructure?.basic_monthly || 0);
  const annualGross = Number(employee.total_allowances || 0) + Number(payStructure?.basic_yearly || 0);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content salary-view-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header salary-view-modal-header">
          <div>
            <span className="section-kicker">Employee salary sheet</span>
            <h5>
              {employee.first_name} {employee.last_name}
            </h5>
          </div>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body salary-view-modal-body">
          {!payStructure ? (
            <p className="text-center">No salary structure defined</p>
          ) : (
            <>
              <div className="salary-view-summary-grid">
                <div className="salary-view-summary-card">
                  <span>Basic Monthly</span>
                  <strong>{formatCurrency(payStructure.basic_monthly)}</strong>
                </div>
                <div className="salary-view-summary-card">
                  <span>Gross Yearly</span>
                  <strong>{formatCurrency(annualGross)}</strong>
                </div>
                <div className="salary-view-summary-card">
                  <span>CTC Yearly</span>
                  <strong>{formatCurrency(payStructure.ctc_yearly)}</strong>
                </div>
              </div>

              <div className="salary-section">
                <div className="salary-grid salary-grid-header">
                  <div></div>
                  <div className="text-center">
                    <strong>Monthly</strong>
                  </div>
                  <div className="text-center">
                    <strong>Annually</strong>
                  </div>
                </div>

                <div className="salary-grid mt-2">
                  <div>
                    <strong>Basic</strong>
                  </div>
                  <div className="text-center">{formatCurrency(payStructure.basic_monthly)}</div>
                  <div className="text-center">{formatCurrency(payStructure.basic_yearly)}</div>
                </div>
              </div>

              {allowances.length > 0 && (
                <div className="salary-section">
                  <div className="salary-grid salary-grid-header">
                    <div>
                      <strong>Allowances</strong>
                    </div>
                    <div className="text-center">
                      <strong>Monthly</strong>
                    </div>
                    <div className="text-center">
                      <strong>Annually</strong>
                    </div>
                  </div>

                  {allowances
                    .filter((allowance) => allowance.flat_amount != null)
                    .map((allowance, index) => (
                      <div key={index} className="salary-grid mt-1 salary-grid-line-item">
                        <div>{allowance.label_detail?.title || "Allowance"}</div>
                        <div className="text-center">{formatCurrency(allowance.flat_amount / 12)}</div>
                        <div className="text-center">{formatCurrency(allowance.flat_amount)}</div>
                      </div>
                    ))}
                </div>
              )}

              <div className="salary-section">
                <div className="salary-grid highlight salary-grid-highlight-card">
                  <div>
                    <strong>Gross Salary</strong>
                  </div>
                  <div className="text-center">{formatCurrency(monthlyGross)}</div>
                  <div className="text-center">{formatCurrency(annualGross)}</div>
                </div>
              </div>

              {deductions.length > 0 && (
                <div className="salary-section">
                  <div className="salary-grid salary-grid-header">
                    <div>
                      <strong>Deductions</strong>
                    </div>
                    <div className="text-center">
                      <strong>Monthly</strong>
                    </div>
                    <div className="text-center">
                      <strong>Annually</strong>
                    </div>
                  </div>

                  {deductions.map((deduction, index) => (
                    <div key={index} className="salary-grid mt-1 salary-grid-line-item">
                      <div>{deduction.label_detail?.title || "Deduction"}</div>
                      <div className="text-center">{formatCurrency(deduction.flat_amount / 12)}</div>
                      <div className="text-center">{formatCurrency(deduction.flat_amount)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="salary-section">
                <div className="salary-grid highlight salary-grid-highlight-card">
                  <div>
                    <strong>CTC (Cost to Company)</strong>
                  </div>
                  <div className="text-center">{formatCurrency(payStructure.ctc_monthly)}</div>
                  <div className="text-center">{formatCurrency(payStructure.ctc_yearly)}</div>
                </div>
              </div>

              <div className="salary-section salary-version-panel">
                <div className="salary-grid">
                  <div>
                    <strong>Version Month</strong>
                  </div>
                  <div className="text-center" style={{ gridColumn: 'span 2' }}>
                    {formatDate(payStructure.effective_from)}
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SalaryList;

